import { useEffect, useState, useCallback, useMemo } from "react";
import API from "../lib/api";
import { toast } from "sonner";
import { Trophy, ChartBar, CurrencyGbp, Lightning, UsersThree } from "@phosphor-icons/react";
import { todayLocalISO } from "../lib/dateUtil";

const fmtGBP = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const METRIC_ROWS = [
  { section: "Bookings" },
  { key: "jobs_created", label: "Jobs booked" },
  { key: "jobs_completed", label: "Jobs completed (their bookings)" },
  { key: "emergency_jobs", label: "Emergency jobs" },
  { key: "projects", label: "Projects created" },
  { key: "job_requests", label: "Job requests" },
  { key: "tasks", label: "Tasks created" },
  { key: "work_orders", label: "Work orders" },
  { section: "Sales & Money" },
  { key: "quotes_raised", label: "Quotes raised" },
  { key: "quotes_won", label: "Quotes won" },
  { key: "quotes_won_value", label: "Value won (approved quotes)", money: true },
  { key: "invoices_raised", label: "Invoices raised" },
  { key: "invoices_value", label: "Value invoiced", money: true },
  { key: "invoices_paid", label: "Invoices paid" },
  { key: "invoices_paid_value", label: "£ collected", money: true },
  { section: "Engagement" },
  { key: "messages_sent", label: "Messages sent" },
  { key: "files_uploaded", label: "Files uploaded" },
  { key: "notes", label: "Notes written" },
  { key: "platform_actions", label: "Platform actions (all activity)" },
];

const PRESETS = [
  { id: "week", label: "This Week" },
  { id: "month", label: "This Month" },
  { id: "quarter", label: "This Quarter" },
  { id: "year", label: "This Year" },
  { id: "all", label: "All Time" },
];

function presetRange(id) {
  const now = new Date();
  const end = todayLocalISO();
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  if (id === "week") {
    const d = new Date(now);
    const day = (d.getDay() + 6) % 7; // Monday start
    d.setDate(d.getDate() - day);
    return { start: iso(d), end };
  }
  if (id === "month") return { start: iso(new Date(now.getFullYear(), now.getMonth(), 1)), end };
  if (id === "quarter") return { start: iso(new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1)), end };
  if (id === "year") return { start: `${now.getFullYear()}-01-01`, end };
  return { start: "", end: "" };
}

const ROLE_BADGE = {
  super_admin: "bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300",
  admin: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
  project_manager: "bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-300",
};

export default function StaffPerformancePage() {
  const [preset, setPreset] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const { start, end } = presetRange(p);
      const { data } = await API.get("/staff-performance", { params: { start, end } });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load staff performance");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(preset); }, [preset, load]);

  const staff = useMemo(() => data?.staff || [], [data]);

  // Leader per metric (for the 🏆 highlight) — ties: no highlight unless single leader
  const leaders = useMemo(() => {
    const out = {};
    for (const row of METRIC_ROWS) {
      if (!row.key) continue;
      let best = 0, bestId = null, tie = false;
      for (const s of staff) {
        const v = s.metrics[row.key] || 0;
        if (v > best) { best = v; bestId = s.user_id; tie = false; }
        else if (v === best && v > 0) tie = true;
      }
      if (bestId && !tie) out[row.key] = bestId;
    }
    return out;
  }, [staff]);

  return (
    <div data-testid="staff-performance-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight flex items-center gap-2">
            <ChartBar size={26} className="text-primary" /> Staff Performance
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Side-by-side work input across all admin staff. Only super admins can see this page.
          </p>
        </div>
        <div className="flex gap-1.5 flex-wrap" data-testid="period-presets">
          {PRESETS.map(p => (
            <button
              key={p.id}
              onClick={() => setPreset(p.id)}
              className={`px-3 h-8 rounded-md text-xs font-medium border transition-colors ${preset === p.id ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/40"}`}
              data-testid={`preset-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Headline cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="border border-border rounded-lg p-4 bg-card" data-testid="card-top-activity">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Lightning size={12} /> Most Active</p>
          <p className="text-lg font-bold mt-1 truncate">{data?.top_activity?.name || "—"}</p>
          <p className="text-[11px] text-muted-foreground">{data?.top_activity ? `${data.top_activity.score} items created` : "no activity in period"}</p>
        </div>
        <div className="border border-border rounded-lg p-4 bg-card" data-testid="card-top-value">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Trophy size={12} /> Top £ Won</p>
          <p className="text-lg font-bold mt-1 truncate">{data?.top_value?.name || "—"}</p>
          <p className="text-[11px] text-muted-foreground">{data?.top_value ? `${fmtGBP(data.top_value.value)} in approved quotes` : "no quotes won in period"}</p>
        </div>
        <div className="border border-border rounded-lg p-4 bg-card" data-testid="card-team-value">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><CurrencyGbp size={12} /> Team Value Won</p>
          <p className="text-lg font-bold mt-1">{fmtGBP(data?.totals?.quotes_won_value)}</p>
          <p className="text-[11px] text-muted-foreground">{fmtGBP(data?.totals?.invoices_paid_value)} collected</p>
        </div>
        <div className="border border-border rounded-lg p-4 bg-card" data-testid="card-team-activity">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><UsersThree size={12} /> Team Output</p>
          <p className="text-lg font-bold mt-1">{(data?.totals?.jobs_created || 0) + (data?.totals?.emergency_jobs || 0)} jobs</p>
          <p className="text-[11px] text-muted-foreground">{data?.totals?.quotes_raised || 0} quotes · {data?.totals?.invoices_raised || 0} invoices</p>
        </div>
      </div>

      {/* Comparison table */}
      {loading ? (
        <p className="text-sm text-muted-foreground p-10 text-center">Loading…</p>
      ) : staff.length === 0 ? (
        <p className="text-sm text-muted-foreground p-10 text-center border border-dashed border-border rounded-lg">No admin staff found.</p>
      ) : (
        <div className="border border-border rounded-lg overflow-x-auto bg-card" data-testid="staff-comparison-table">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-muted-foreground w-56">Metric</th>
                {staff.map(s => (
                  <th key={s.user_id} className="px-4 py-3 text-center" data-testid={`staff-col-${s.user_id}`}>
                    <div className="flex flex-col items-center gap-1">
                      <span className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                        {(s.name || "?").slice(0, 1).toUpperCase()}
                      </span>
                      <span className="text-xs font-semibold truncate max-w-[110px]">{s.name}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium capitalize ${ROLE_BADGE[s.role] || "bg-muted text-muted-foreground"}`}>
                        {s.role.replace("_", " ")}
                      </span>
                    </div>
                  </th>
                ))}
                <th className="px-4 py-3 text-center text-[10px] uppercase tracking-wider text-muted-foreground">Team Total</th>
              </tr>
            </thead>
            <tbody>
              {METRIC_ROWS.map((row, i) => row.section ? (
                <tr key={`s-${i}`} className="bg-muted/30">
                  <td colSpan={staff.length + 2} className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{row.section}</td>
                </tr>
              ) : (
                <tr key={row.key} className="border-b border-border/60 last:border-0">
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{row.label}</td>
                  {staff.map(s => {
                    const v = s.metrics[row.key] || 0;
                    const isLeader = leaders[row.key] === s.user_id;
                    return (
                      <td key={s.user_id} className="px-4 py-2.5 text-center" data-testid={`cell-${row.key}-${s.user_id}`}>
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${isLeader ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" : v === 0 ? "text-muted-foreground/50" : ""}`}>
                          {isLeader && <Trophy size={11} weight="fill" className="text-amber-500" />}
                          {row.money ? fmtGBP(v) : v}
                        </span>
                      </td>
                    );
                  })}
                  <td className="px-4 py-2.5 text-center text-xs font-bold">
                    {row.money ? fmtGBP(data?.totals?.[row.key]) : (data?.totals?.[row.key] || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[10px] text-muted-foreground mt-3">
        "Value won" counts a quote's value only once it's approved or converted, credited to the staff member who raised it. "£ collected" counts invoices marked paid in the period.
      </p>
    </div>
  );
}
